import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import {
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';
import ChartContext from './DataEntrySection/DataEntryProvider';
import { getSigDigitsUtil } from '../util/util';

function SQCScatterPlot({
  collections,
  xMin,
  xMax,
  yMin,
  yMax,
  chartSigDigits,
  Xlabel,
  Ylabel,
  unit
}) {
  const { currentControlStrategyConfigurations } = useContext(ChartContext);

  const renderSigDigits = (stringValue) => {
    return getSigDigitsUtil(
      stringValue,
      currentControlStrategyConfigurations?.chartSigDigits || chartSigDigits || 3
    );
  };

  const formatAxis = (tickItem) => {
    return renderSigDigits(tickItem);
  };

  const CustomScatterTooltip = ({ active, payload }) => {
    if (active && payload && payload.length > 0) {
      // Find the matching data point in collections[0].data by x and y
      const { x, y } = payload[0].payload;
      const dot = collections[0].data.find(
        (d) =>
          Math.abs(Number(d.x) - Number(x)) < 0.000001 &&
          Math.abs(Number(d.y) - Number(y)) < 0.000001
      );
      if (dot) {
        let parameter = '';
        if (dot.fullKey) {
          const parts = dot.fullKey.split('|');
          parameter = parts[parts.length - 1];
        } else if (dot.parameter) {
          parameter = dot.parameter;
        }
        return (
          <div style={{ background: '#fff', border: '1px solid #ccc', padding: 8 }}>
            <div>{parameter}</div>
            <div>Quartiles: {dot.x}</div>
            <div>y: {dot.y}</div>
          </div>
        );
      }
    }
    return null;
  };
  const xSpan = xMax - xMin;
  const ySpan = yMax - yMin;

  const uniqueParameters = currentControlStrategyConfigurations?.fullKey
    ? [currentControlStrategyConfigurations.fullKey.split('|').pop()]
    : undefined;

  return (
    <>
      <h5>
        Q/Q Plot
        {uniqueParameters?.length
          ? `: ${uniqueParameters.join(', ')}${unit?.trim() ? `, ${unit}` : ''}`
          : unit?.trim()
            ? `: ${unit}`
            : ''}
      </h5>

      <ResponsiveContainer height={400}>
        <ScatterChart
          margin={{
            top: 5,
            right: 20,
            bottom: 15,
            left: 20
          }}
        >
          {collections.map((collection) => (
            <Scatter
              name={collection.name}
              data={collection.data}
              key={collection.name}
              fill={collection.color}
              line={collection.line}
              dataKey="index"
            />
          ))}

          <CartesianGrid stroke="#ccc" strokeDasharray="5 5" />
          <XAxis
            type="number"
            dataKey="x"
            name="Quartiles"
            domain={[xMin - 0.1 * xSpan, xMax + 0.1 * xSpan]}
            tickFormatter={formatAxis}
            label={{
              value: typeof Xlabel === 'string' ? Xlabel : Xlabel?.props?.children || '',
              position: 'insideBottom',
              offset: -5,
              dy: 10
            }}
          />
          <YAxis
            type="number"
            dataKey="y"
            domain={[yMin - 0.1 * ySpan, yMax + 0.1 * ySpan]}
            tickFormatter={formatAxis}
            label={{
              value: typeof Ylabel === 'string' ? Ylabel : Ylabel?.props?.children || '',
              angle: -90,
              position: 'insideLeft',
              dx: -10,
              dy: 40
            }}
          />

          <Legend verticalAlign="top" height={36} />
          <Tooltip content={<CustomScatterTooltip />} />
        </ScatterChart>
      </ResponsiveContainer>
    </>
  );
}

SQCScatterPlot.propTypes = {
  collections: PropTypes.arrayOf(PropTypes.object).isRequired,
  xMin: PropTypes.number.isRequired,
  xMax: PropTypes.number.isRequired,
  yMin: PropTypes.number.isRequired,
  yMax: PropTypes.number.isRequired,
  Xlabel: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  Ylabel: PropTypes.oneOfType([PropTypes.string, PropTypes.element])
};

export default SQCScatterPlot;
